import { captureTransforms } from "./captureTransforms";

const loadHtml2Canvas = () =>
  new Promise((resolve, reject) => {
    if (window.html2canvas) return resolve();

    const existing = document.getElementById("html2canvasScript");
    if (existing) {
      existing.addEventListener("load", resolve);
      existing.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = "html2canvasScript";
    script.src =
      "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js";
    script.onload = resolve;
    script.onerror = () => reject(new Error("Failed to load html2canvas"));
    document.head.appendChild(script);
  });

/**
 * Captures a widget container element as a full-size PNG image and triggers a
 * browser download. Handles:
 *   - Content clipped by overflow / max-height  (expanded before capture)
 *   - Mid-scroll position                        (reset to 0 then restored)
 *
 * @param {HTMLElement} containerElement
 * @param {string} [filename="widget"]
 * @param {string} [widgetType] - widget comp name, used for per-widget transforms
 */
export const downloadWidgetAsImage = async (
  containerElement,
  filename = "widget",
  widgetType
) => {
  await loadHtml2Canvas();

  // ── 1. Expand overflow / max-height constraints ──────────────────────────
  const overridden = [];
  [containerElement, ...containerElement.querySelectorAll("*")].forEach((el) => {
    const computed = window.getComputedStyle(el);
    const hasInlineMaxHeight = !!el.style.maxHeight;
    const scrollValues = ["auto", "scroll"];
    const hasScrollOverflow =
      scrollValues.includes(computed.overflow) ||
      scrollValues.includes(computed.overflowY) ||
      scrollValues.includes(computed.overflowX);

    if (hasInlineMaxHeight || hasScrollOverflow) {
      overridden.push({
        el,
        originalStyle: {
          overflow: el.style.overflow,
          overflowX: el.style.overflowX,
          overflowY: el.style.overflowY,
          maxHeight: el.style.maxHeight,
          height: el.style.height,
        },
        scrollTop: el.scrollTop,
        scrollLeft: el.scrollLeft,
      });
      el.scrollTop = 0;
      el.scrollLeft = 0;
      el.style.overflow = "visible";
      el.style.overflowX = "visible";
      el.style.overflowY = "visible";
      if (hasInlineMaxHeight) {
        el.style.maxHeight = "none";
        // Only force height:auto when content is actually clipped. A
        // fixed-height element whose children use percentage heights (the pie
        // chart's ResponsiveContainer) collapses to 0 under height:auto.
        if (el.scrollHeight > el.clientHeight + 1) {
          el.style.height = "auto";
        }
      }
    }
  });

  // ── 1b. Hide scroll-fade gradient overlays ────────────────────────────────
  const hiddenEls = containerElement.querySelectorAll("[data-hide-on-capture]");
  hiddenEls.forEach((el) => {
    el._prevDisplay = el.style.display;
    el.style.display = "none";
  });

  // ── 1c. Hide action buttons (settings, download, etc.) on all widgets ────
  const actionEls = containerElement.querySelectorAll(
    '[id^="widget-actions-"], [id^="widget-download-"]'
  );
  actionEls.forEach((el) => {
    el._prevDisplay = el.style.display;
    el.style.display = "none";
  });

  // ── 1d. Widget-specific transforms (may be async) ──────────────────────────
  const restoreWidgetTransform = widgetType && captureTransforms[widgetType]
    ? await captureTransforms[widgetType](containerElement)
    : () => {};

  // Wait for the DOM to reflow to its full expanded size
  await new Promise((resolve) => requestAnimationFrame(resolve));

  // ── 1e. Expand container to fit content + add right padding ───────────────
  const IMAGE_PADDING_RIGHT = 16; // px
  const savedContainerDims = {
    width: containerElement.style.width,
    minWidth: containerElement.style.minWidth,
    paddingRight: containerElement.style.paddingRight,
  };
  containerElement.style.minWidth = "max-content";
  containerElement.style.paddingRight = IMAGE_PADDING_RIGHT + "px";

  // Another reflow for container size change
  await new Promise((resolve) => requestAnimationFrame(resolve));

  const captureWidth = containerElement.scrollWidth;
  const captureHeight = containerElement.scrollHeight;
  const MAX_CANVAS_DIM = 16384;
  const scale = Math.max(1, Math.min(2, MAX_CANVAS_DIM / captureWidth, MAX_CANVAS_DIM / captureHeight));

  console.log("[downloadWidgetAsImage]", { captureWidth, captureHeight, scale });

  try {
    const canvas = await window.html2canvas(containerElement, {
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      width: captureWidth,
      height: captureHeight,
    });

    const link = document.createElement("a");
    link.download = `${filename}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } finally {
    // ── 2. Restore overflow / scroll positions ────────────────────────────
    overridden.forEach(({ el, originalStyle, scrollTop, scrollLeft }) => {
      Object.assign(el.style, originalStyle);
      el.scrollTop = scrollTop;
      el.scrollLeft = scrollLeft;
    });
    // ── 2b. Restore hidden gradient overlays ────────────────────────────
    hiddenEls.forEach((el) => {
      el.style.display = el._prevDisplay;
      delete el._prevDisplay;
    });
    // ── 2c. Restore action buttons ──────────────────────────────────────
    actionEls.forEach((el) => {
      el.style.display = el._prevDisplay;
      delete el._prevDisplay;
    });
    // ── 2d. Restore container dimensions ────────────────────────────────
    containerElement.style.width = savedContainerDims.width;
    containerElement.style.minWidth = savedContainerDims.minWidth;
    containerElement.style.paddingRight = savedContainerDims.paddingRight;
    // ── 2e. Restore widget-specific transforms ──────────────────────────
    restoreWidgetTransform();
  }
};
